/**
 * Address detection and geocoding for the map view.
 *
 * Supports:
 * - Google Maps URLs (extracts lat/lng from @lat,lng in the URL, or place query)
 * - Plain addresses (geocoded via Nominatim / OpenStreetMap)
 */

export type LatLng = { lat: number; lng: number }

/** Patterns that suggest a Google Maps URL */
const MAPS_URL_RE =
  /https?:\/\/(www\.)?(maps\.google\.com|google\.com\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i

/** lat,lng embedded in a Google Maps URL like /@37.3,-122.0,15z */
const AT_LATLNG_RE = /@(-?\d+\.\d+),(-?\d+\.\d+)/

/** Google Maps place or search query param */
const MAPS_QUERY_RE = /[?&]q=([^&]+)/

/** Heuristic: looks like a street address (number + street name + city/state) */
const ADDRESS_RE = /^\d{1,5}\s+\w[\w\s.]+,\s*\w/

export function isGoogleMapsUrl(text: string): boolean {
  return MAPS_URL_RE.test(text)
}

/** Extract lat/lng directly from a Google Maps URL if the coordinates are embedded. */
export function extractCoordsFromMapsUrl(url: string): LatLng | null {
  const match = AT_LATLNG_RE.exec(url)
  if (match && match[1] != null && match[2] != null) {
    return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) }
  }
  return null
}

/** Extract the search query from a Google Maps URL (for geocoding). */
export function extractQueryFromMapsUrl(url: string): string | null {
  const match = MAPS_QUERY_RE.exec(url)
  if (match?.[1]) return decodeURIComponent(match[1].replace(/\+/g, " "))
  // Try /place/QUERY/ form
  const placeMatch = /\/place\/([^/@]+)/.exec(url)
  if (placeMatch?.[1]) return decodeURIComponent(placeMatch[1].replace(/\+/g, " "))
  return null
}

export function looksLikeAddress(text: string): boolean {
  return ADDRESS_RE.test(text.trim())
}

/** Geocode a query string via Nominatim. Returns null if not found. */
export async function geocodeAddress(query: string): Promise<LatLng | null> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`
  try {
    const resp = await fetch(url, {
      headers: { "Accept-Language": "en", "User-Agent": "druthers-map-view/1.0" },
    })
    if (!resp.ok) return null
    const results = await resp.json()
    if (!results.length || !results[0]) return null
    const first = results[0] as { lat: string; lon: string }
    return { lat: parseFloat(first.lat), lng: parseFloat(first.lon) }
  } catch {
    return null
  }
}

export type GeocodedItem = {
  id: string
  label: string
  latlng: LatLng
}

/**
 * Attempt to resolve a candidate's content to a lat/lng.
 * For Google Maps URLs: extract coords directly or geocode the place query.
 * For plain addresses: geocode via Nominatim.
 * Returns null if we can't locate it.
 */
export async function resolveItem(
  id: string,
  content: string,
): Promise<GeocodedItem | null> {
  const trimmed = content.trim()

  if (isGoogleMapsUrl(trimmed)) {
    const direct = extractCoordsFromMapsUrl(trimmed)
    if (direct) return { id, label: trimmed, latlng: direct }
    const query = extractQueryFromMapsUrl(trimmed)
    if (query) {
      const latlng = await geocodeAddress(query)
      if (latlng) return { id, label: query, latlng }
    }
    return null
  }

  if (looksLikeAddress(trimmed)) {
    const latlng = await geocodeAddress(trimmed)
    if (latlng) return { id, label: trimmed, latlng }
  }

  return null
}
